import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { ReactComponent as ExclamationImg } from '../../../../../../../../img/mrb-exclamation.svg';
import { ReactComponent as GreyClockImg } from '../../../../../../../../img/mrb-g-clock.svg';
import { ReactComponent as YellowClockImg } from '../../../../../../../../img/mrb-y-clock.svg';
import './style.scss';
import Utils from '../../../../../../../Services/Utils';
import Tooltip from '../../../../../../Common/Tooltip';

export const TURN_STATUS_OK = 1;
export const TURN_STATUS_WARNING = 2;
export const TURN_STATUS_DANGER = 3;

/**
 * @callback onExpandCb
 * @param {boolean} isExpanded
 */

/**
 * Calendar workflow line
 * @param {MRB.Result} unit
 * @param {boolean} expanded
 * @param {onExpandCb} onExpand
 */
function CalendarBodyLineUnit({ unit, expanded, onExpand }) {
  const getLastWorkflowDate = useCallback(
    () =>
      unit?.workflow_instances?.reduce(
        (a, workflow) =>
          Math.max(
            a,
            workflow?.action_items?.reduce((b, action) => Math.max(b, +new Date(action.expected_completion_date) || 0) || 0, 0)
          ),
        0
      ),
    [unit?.workflow_instances]
  );

  const identification = useMemo(() => `${unit.number} - ${unit.building}`, [unit.building, unit.number]);
  const unitType = useMemo(() => unit.unit_type, [unit.unit_type]);
  const turnStatus = useMemo(() => unit.turn_status, [unit.turn_status]);
  const daysProgress = useMemo(() => !Utils.isNull(unit.days_in_progress) || unit.days_in_progress >= 0 ? unit.days_in_progress :  '---', [unit.days_in_progress]);
  const daysCompletion = useMemo(() => !Utils.isNull(unit.days_until_completion) && unit.days_until_completion, [unit.days_until_completion]);
  const moveInDate = useMemo(() => !Utils.isNull(unit.move_in_date) ? moment(unit.move_in_date, 'YYYY/MM/DD').format('MM/DD/YYYY') : '---', [unit.move_in_date]);
  const moveOutDate = useMemo(() => !Utils.isNull(unit.expected_completion_date) ? moment(unit.expected_completion_date, 'YYYY/MM/DD').format('MM/DD/YYYY') : '---', [unit.expected_completion_date]);
  const iconStatus = useMemo(() => {
    const imgProps = {
      width: '20px',
      height: '20px'
    };
    if (turnStatus === TURN_STATUS_WARNING) {
      return <YellowClockImg {...imgProps} />;
    } else if (moveInDate !== '---' && turnStatus === TURN_STATUS_DANGER) {
      return <ExclamationImg {...imgProps} />;
    }
    return <GreyClockImg {...imgProps} />;
  }, [getLastWorkflowDate, moveInDate]);

  const handleClick = useCallback(() => {
    if (onExpand) {
      onExpand(!expanded);
    }
  }, [onExpand, expanded]);

  return (
    <div className="unit_column calendar_unit" onClick={handleClick}>
      <Tooltip message={'Describes the relationship between the Unit Turn Workflows and the following Move-In date.'} classParent="calendar_unit__status" className="Tooltip_Blue">
        {iconStatus}
      </Tooltip>
      <div className="calendar_unit__main">
        <Tooltip message={'Unit identification.'} className="Tooltip_Blue">
          <div className="calendar_unit__main--identification">{identification}</div>
          <div className="calendar_unit__main--type">{unitType}</div>
        </Tooltip>
        {expanded && (
          <div className="calendar_unit__main--days">
            <Tooltip
              message={'Number of days a Unit turn has been in progress.'}
              classParent="calendar_unit__main--days--progress"
              className="Tooltip_Blue">
              {daysProgress}
            </Tooltip>
            <Tooltip
              message={'Number of days until the expected Unit Workflow completion.'}
              classParent="calendar_unit__main--days--completion"
              className="Tooltip_Blue">
              {daysCompletion}
            </Tooltip>
            
          </div>
        )}
      </div>
      <div className="calendar_unit__dates">
        <Tooltip message={'Expected move in date of the next tenant.'} classParent="calendar_unit__dates--move-in" className="Tooltip_Blue">
          MI {moveInDate}
        </Tooltip>
        {expanded && (
          <Tooltip
            message={'Latest Expected Completion date amongst all the tasks included in the Unit Workflow.'}
            classParent="calendar_unit__dates--move-out"
            className="Tooltip_Blue">
            EC {moveOutDate}
          </Tooltip>
        )}
      </div>
    </div>
  );
}

CalendarBodyLineUnit.propTypes = {
  unit: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  onExpand: PropTypes.func
};

export default React.memo(CalendarBodyLineUnit);
